import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Database } from 'lucide-react';
import clsx from 'clsx';
import PageLayout from '@components/layout/PageLayout';
import Tabs from '@components/common/Tabs';
import ErrorBoundary from '@components/common/ErrorBoundary';
import ManualImport from '@components/features/import/ManualImport';
import { ImportJobsTab } from '@components/features/import-jobs';
import { SettingsLayout } from '@components/features/data-management-settings';
import {
  DATA_MANAGEMENT_TOP_TABS,
  SETTINGS_TAB,
  isLegacySettingsTab,
  resolveLegacySection,
  resolveTopTab,
} from '@components/features/data-management-settings/settingsNav';
import styles from './AdminDataManagementPage.module.css';

const AdminDataManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab = resolveTopTab(requestedTab);

  useEffect(() => {
    if (!isLegacySettingsTab(requestedTab)) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.set('tab', SETTINGS_TAB);
    next.set('section', resolveLegacySection(requestedTab, searchParams.get('section')));
    setSearchParams(next, { replace: true });
  }, [requestedTab, searchParams, setSearchParams]);

  const setActiveTab = (tabKey: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabKey);
    if (tabKey !== SETTINGS_TAB) {
      next.delete('section');
    } else if (!next.get('section')) {
      next.set('section', resolveLegacySection(tabKey, null));
    }
    setSearchParams(next, { replace: true });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'import-jobs':
        return (
          <ErrorBoundary level="page">
            <ImportJobsTab />
          </ErrorBoundary>
        );
      case SETTINGS_TAB:
        return (
          <ErrorBoundary level="page">
            <SettingsLayout />
          </ErrorBoundary>
        );
      case 'manual-import':
      default:
        return (
          <ErrorBoundary level="page">
            <ManualImport />
          </ErrorBoundary>
        );
    }
  };

  return (
    <PageLayout title="Data Management" showHeader={false}>
      <div className={styles.header} data-test-id="data-management-page">
        <h1 className={styles.title}>
          <Database size={24} />
          Data Management
        </h1>
      </div>

      <div className={styles.container}>
        <Tabs
          items={[...DATA_MANAGEMENT_TOP_TABS]}
          activeTab={activeTab === SETTINGS_TAB ? SETTINGS_TAB : activeTab}
          onTabChange={setActiveTab}
          className={styles.tabs}
        />

        <div className={clsx(styles.content, activeTab === SETTINGS_TAB && styles.contentFlush)}>
          {renderTabContent()}
        </div>
      </div>
    </PageLayout>
  );
};

export default AdminDataManagementPage;
